var _sentryCollisionFreeGlobalObject = typeof window === "undefined" ? global : window;
_sentryCollisionFreeGlobalObject["__sentryRewritesTunnelPath__"] = undefined;
_sentryCollisionFreeGlobalObject["SENTRY_RELEASE"] = {"id":"IUTOHX96zczWa3ja2usMv"};
_sentryCollisionFreeGlobalObject["__rewriteFramesAssetPrefixPath__"] = "/builds/prod/21978986296_4038_20260213T075103";

import { convert } from '@revenuemarket/utils';
import * as Sentry from '@sentry/nextjs';
import { getCookie } from 'cookies-next';
import { deserializeAccessToken } from '~utils/auth';

const BROWSER_EXTENSION_FILENAME_MATCHERS = [
  // Chrome 확장프로그램이 주입하는 스크립트(예: MetaMask)는 우리 코드로 해결할 수 없는 노이즈가 많습니다.
  (filename) => filename.startsWith('chrome-extension://'),
  // Firefox 확장프로그램이 주입하는 스크립트.
  (filename) => filename.startsWith('moz-extension://'),
  // Safari 확장프로그램이 주입하는 스크립트.
  (filename) => filename.startsWith('safari-extension://'),
  // 일부 스택트레이스는 확장프로그램 프레임을 `extensions::` 같은 pseudo URL로 기록합니다.
  (filename) => filename.includes('extensions::'),
  // MetaMask 등은 `inpage.js`를 주입하며, 종종 `app:///inpage.js` 형태로도 보입니다.
  (filename) => /(^|\/)inpage\.js$/i.test(filename),
  // 일반적인 확장프로그램 content script.
  (filename) => /(^|\/)content-script\.js$/i.test(filename),
  // Sentry에서 다량 관측된 확장프로그램 노이즈: `frame_ant/...` 프레임 (PR #4272, 2026-02-10).
  (filename) => /(^|\/)frame_ant(\/|$)/i.test(filename),
];

const isBrowserExtensionFilename = (filename) => {
  return BROWSER_EXTENSION_FILENAME_MATCHERS.some((match) => match(filename));
};

const eventHasBrowserExtensionFrames = (event) => {
  if (!event?.exception?.values) return false;

  for (const exception of event.exception.values) {
    const frames = exception?.stacktrace?.frames;
    if (!frames) continue;

    for (const frame of frames) {
      const filename = frame?.filename;
      if (!filename) continue;

      // 브라우저 확장프로그램은 페이지 컨텍스트에 스크립트를 주입합니다(예: MetaMask inpage.js).
      // 이런 에러는 제품 코드에서 대응 불가한 경우가 많고, `app:///inpage.js` 같은 형태로 노이즈가 발생합니다.
      if (isBrowserExtensionFilename(filename)) {
        return true;
      }
    }
  }

  return false;
};

// Ignore 규칙은 여기에 추가합니다.
// 추후 재검토 가능하도록 "무엇을/왜" + 근거(이슈/PR/날짜)를 함께 남기고, 규칙은 최대한 좁게 유지합니다.
const IGNORE_RULES = [
  {
    // 확장프로그램 프레임 + "func sseError not found" 노이즈: 제품 코드로 해결 불가/비액션성 케이스가 대부분.
    name: 'browser_extension_func_sseError_not_found',
    match: ({ errorMessage, event }) => {
      return eventHasBrowserExtensionFrames(event) || /func sseError not found/i.test(errorMessage);
    },
  },
  {
    // 일부 모바일/인앱 브라우저의 비밀번호 매니저 스크립트가 cross-origin frame 접근 시도 중 발생하는 노이즈.
    // 예: extractFormsAndFormElements_, hasPasswordField_ (REVENUE-MARKET-22W/22X/2K5)
    name: 'browser_extension_cross_origin_frame_probe',
    match: ({ errorMessage, event }) => {
      if (!/Blocked a frame with origin/i.test(errorMessage)) return false;
      const culprit = event?.culprit ?? '';
      return /extractFormsAndFormElements_|hasPasswordField_/i.test(culprit);
    },
  },
  {
    // Next.js client invariant: 동일 URL로 hard navigate 시도. 대체로 무해하고 발생량이 많아 노이즈가 됩니다.
    name: 'nextjs_invariant_hard_navigate_same',
    match: ({ errorMessage }) => {
      return errorMessage.includes('Invariant: attempted to hard navigate to the same');
    },
  },
  {
    // 라우팅/언마운트 중 요청 취소로 발생하는 케이스가 많아 기대 가능한 에러(노이즈).
    name: 'request_aborted',
    match: ({ errorMessage }) => {
      return errorMessage.includes('Request aborted');
    },
  },
];

Sentry.init({
  dsn: 'https://fd1faa5f2d6b45dbbe0d55a832bbfb27@o4504325876547584.ingest.sentry.io/4504325877334016',
  release: process.env.RELEASE_ID,
  environment: process.env.APP_ENV,
  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for performance monitoring.
  // We recommend adjusting this value in production
  // Sentry SDK가 `beforeSend`보다 먼저 적용하는(메시지 기반) 필터링 규칙입니다.
  ignoreErrors: [
    // 클라이언트 네트워크 에러(오프라인/광고차단/요청 취소 등)로 흔히 발생하며 액션성 낮음.
    /Network Error/i,
    // 브라우저 환경 노이즈(예: ResizeObserver loop limit exceeded). PR #4272에서 추가(2026-02-10).
    /ResizeObserver loop/i,
  ],
  beforeSend(event) {
    // Set company ID role from token
    const accessToken = convert(getCookie('accessToken'), String);
    if (accessToken) {
      const decodedToken = deserializeAccessToken(accessToken);

      if (decodedToken?.companyIdRole?.id) {
        event.tags = {
          ...event.tags,
          companyId: decodedToken.companyIdRole.id,
        };
      }
    }

    if (event.exception && event.exception.values) {
      const errorMessage = event.exception.values[0]?.value ?? '';

      if (IGNORE_RULES.some((rule) => rule.match({ errorMessage, event }))) {
        return null;
      }

      return event;
    }
    return event;
  },
  tracesSampler: (samplingContext) => {
    if (samplingContext.transactionContext.name.includes('/signin')) {
      return 1;
    }
    return 0.2;
  },
  profilesSampleRate: 0.2,
  enabled: Boolean(process.env.APP_ENV) && process.env.APP_ENV !== 'local',
});

Sentry.setTag('role', 'service');
